import * as yup from "yup";
import { Formik } from "formik";
import { Col, FloatingLabel, Form, Row, Container } from 'react-bootstrap';
import ReactTextareaAutosize from 'react-textarea-autosize';
import { ChargeCalculation, ChargeType, ServiceModel } from '../../data/entities';
import { ImageService, ServiceService, TenantService} from '../../services';
import { sharedProperty, useTenantFeatures, useUrlParams } from '../../hooks';
import { FormSectionDivider, FormSectionHeader, FormToggleLabel, HandleDropDownListChange, MediaUploader, SearchableChargeCalcDropdown, SearchableChargeTypeDropdown } from '../../components';
import { forwardRef, useImperativeHandle, useRef } from "react";
import { formatPrice } from '../../utils';
import { useMedia } from './hooks';

interface ServiceDetailsPageProps {
  dataItemState: sharedProperty<ServiceModel>;
  showState: sharedProperty<boolean>;
  onSubmit: any;
}

export const ServiceDetailsPage = forwardRef((props: Readonly<ServiceDetailsPageProps>, ref) =>{

  const id = parseInt(useUrlParams("id")); // ServiceType id
  const { managePricing } = useTenantFeatures();
  const formikRef = useRef<any>(null);

  const { image, uploadImage, removeImage, fetchImage } = useMedia({
    serviceId: props.dataItemState.value.id,
    onUploadSuccess: async () => {
      await fetchImage();
    },
  });
  useImperativeHandle(ref, ()=>({
    submit: () => {
      formikRef.current?.handleSubmit();
    }
  }));

  const validationSchema = yup.object().shape({
    name: yup.string().required("Service Name is required"),
    description: yup.string().notRequired(),
    sku: yup.string().required("SKU is required"),
    defaultPrice: yup.number().required("Default price is required").min(0, "Price must be greater than or equal to 0"),
    defaultChargeType: yup.string().oneOf([ChargeType.Hold, ChargeType.Charge], "Please select a valid charge type"),
    defaultChargeCalculation:yup.string().oneOf([ChargeCalculation.Fixed, ChargeCalculation.Percentage], "Please select a valid charge type"),
    defaultChargeAmount: yup.number().required("Charge is required").min(0, "Charge must be greater than or equal to 0"),
    hideProviders: yup.boolean().notRequired(),
    allowAnyProvider: yup.boolean().notRequired(),
    defaultDuration: yup.number().required("Default duration is required").min(1, "Duration must be greater than 0"),
    defaultBufferDuration: yup.number().required("Default buffer duration is required")
  });

  return (
      <Formik
        innerRef={formikRef}
        enableReinitialize={true}
        validationSchema={validationSchema}
        initialValues={{
          name: props.dataItemState.value.name,
          description: props.dataItemState.value.description,
          sku: props.dataItemState.value.sku,
          defaultPrice: props.dataItemState.value.defaultPrice,
          defaultChargeAmount: props.dataItemState.value.defaultChargeAmount,
          defaultChargeType: props.dataItemState.value.defaultChargeType,
          defaultChargeCalculation: props.dataItemState.value.defaultChargeCalculation,
          hideProviders: props.dataItemState.value.hideProviders,
          allowAnyProvider: props.dataItemState.value.allowAnyProvider,
          defaultDuration: props.dataItemState.value.defaultDuration,
          defaultBufferDuration: props.dataItemState.value.defaultBufferDuration
        }}
        onSubmit={async (values) => {
          const updatedService = await new ServiceService().get(props.dataItemState.value.id);
          const updatedImageId = await new ImageService().get(updatedService?.imageId!);
          const service = {
            ...values,
            id: props.dataItemState.value.id,
            serviceTypeId: id,
            tenantId: TenantService.getTenantId(),
            allowAnyProvider: values.allowAnyProvider,
            imageId: updatedImageId?.id,
          } as ServiceModel;
          props.onSubmit(service);
        }}
      >
        {({ values, setFieldValue, handleSubmit, handleChange, touched, errors }) => {
          return (
            <Form onSubmit={handleSubmit} className="h-100 d-flex flex-column justify-content-between">
              <Container className="px-4" style={{ marginTop: "1rem" }}>

                {/* Basics */}
                <FormSectionHeader>Basics</FormSectionHeader>
                <Row>
                  <Col xs={24} md={16}>
                    <Form.Group controlId="serviceName">
                      <FloatingLabel label="Service Name" className="mb-3">
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="Service Name"
                          value={values.name}
                          onChange={handleChange}
                          isInvalid={touched.name && !!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Group controlId="sku">
                      <FloatingLabel label="SKU" className="mb-3">
                        <Form.Control
                          type="text"
                          name="sKU"
                          placeholder="SKU"
                          value={values.sku}
                          onChange={handleChange}
                          disabled={true}
                          isInvalid={touched.sku && !!errors.sku}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.sku}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group controlId="description">
                      <FloatingLabel label="Description" className="mb-3">
                        <ReactTextareaAutosize
                          minRows={3}
                          className="form-control"
                          placeholder="Description"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>

                <FormSectionDivider />

                {/* Pricing */}
                <FormSectionHeader>Pricing</FormSectionHeader>
                {managePricing ? (
                <>
                <Row>
                  <Col xs={24} md={12}>
                    <Form.Group controlId="defaultPrice">
                      <FloatingLabel label="Default Price" className="mb-3">
                        <Form.Control
                          type="number"
                          name="defaultPrice"
                          placeholder="Default Price"
                          value={values.defaultPrice}
                          onChange={handleChange}
                          isInvalid={touched.defaultPrice && !!errors.defaultPrice}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.defaultPrice}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Group controlId="defaultChargeType">
                      <SearchableChargeTypeDropdown
                        name="defaultChargeType"
                        value={values.defaultChargeType}
                        placeholder="Charge Type"
                        onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                        isInvalid={touched.defaultChargeType && !!errors.defaultChargeType}
                      >
                        <Form.Control.Feedback type="invalid">
                          {errors.defaultChargeType}
                        </Form.Control.Feedback>
                      </SearchableChargeTypeDropdown>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} md={12}>
                    <Form.Group controlId="defaultChargeCalculation">
                      <SearchableChargeCalcDropdown
                        name="defaultChargeCalculation"
                        value={values.defaultChargeCalculation}
                        onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                        isInvalid={touched.defaultChargeCalculation && !!errors.defaultChargeCalculation}
                      >
                        <Form.Control.Feedback type="invalid">
                          {errors.defaultChargeCalculation}
                        </Form.Control.Feedback>
                      </SearchableChargeCalcDropdown>
                    </Form.Group>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Group controlId="defaultChargeAmount">
                      <FloatingLabel label="Charge Amount" className="mb-3">
                        <Form.Control
                          type="number"
                          name="defaultChargeAmount"
                          placeholder="Charge Amount"
                          value={values.defaultChargeAmount}
                          onChange={handleChange}
                          isInvalid={touched.defaultChargeAmount && !!errors.defaultChargeAmount}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.defaultChargeAmount}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>

                </>
                ) : (
                  <Row className="mb-2">
                    <Col xs={12} md={6}>
                      <Form.Label className="small text-body-secondary mb-1">Default Price</Form.Label>
                      <Form.Control
                        plaintext
                        readOnly
                        className="fw-semibold p-0"
                        value={formatPrice(Number(values.defaultPrice ?? 0))}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Label className="small text-body-secondary mb-1">Charge Type</Form.Label>
                      <Form.Control
                        plaintext
                        readOnly
                        className="fw-semibold p-0"
                        value={values.defaultChargeType ?? "—"}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Label className="small text-body-secondary mb-1">Charge Calculation</Form.Label>
                      <Form.Control
                        plaintext
                        readOnly
                        className="fw-semibold p-0"
                        value={values.defaultChargeCalculation ?? "—"}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Label className="small text-body-secondary mb-1">Charge Amount</Form.Label>
                      <Form.Control
                        plaintext
                        readOnly
                        className="fw-semibold p-0"
                        value={values.defaultChargeCalculation === ChargeCalculation.Fixed
                          ? formatPrice(Number(values.defaultChargeAmount ?? 0))
                          : `${values.defaultChargeAmount ?? 0}%`}
                      />
                    </Col>
                  </Row>
                )}

                <FormSectionDivider />

                {/* Scheduling */}
                <FormSectionHeader>Scheduling</FormSectionHeader>
                <Row>
                  <Col xs={24} md={12}>
                    <Form.Group controlId="defaultDuration">
                      <FloatingLabel label="Default Duration (min)" className="mb-3">
                        <Form.Control
                          type="number"
                          name="defaultDuration"
                          placeholder="Default Duration"
                          value={values.defaultDuration}
                          onChange={handleChange}
                          isInvalid={touched.defaultDuration && !!errors.defaultDuration}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.defaultDuration}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Group controlId="defaultBufferDuration">
                      <FloatingLabel label="Default Buffer Duration (min)" className="mb-3">
                        <Form.Control
                          type="number"
                          name="defaultBufferDuration"
                          placeholder="Default Buffer Duration"
                          value={values.defaultBufferDuration}
                          onChange={handleChange}
                          isInvalid={touched.defaultBufferDuration && !!errors.defaultBufferDuration}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.defaultBufferDuration}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>

                <FormSectionDivider />

                {/* Provider Settings */}
                <FormSectionHeader>Provider Settings</FormSectionHeader>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <FormToggleLabel
                    title="Hide Providers"
                    description="Providers will not be shown to customers during booking"
                  />
                  <div className="form-check form-switch fs-4 ms-3" style={{ flexShrink: 0, paddingLeft: 0 }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={values.hideProviders}
                      onChange={() => setFieldValue("hideProviders", !values.hideProviders)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>

                <FormSectionDivider subtle />

                <div className="d-flex align-items-center justify-content-between mb-3">
                  <FormToggleLabel
                    title="Allow Any Provider"
                    description='Customers can select "Any Provider" and the first available will be assigned'
                  />
                  <div className="form-check form-switch fs-4 ms-3" style={{ flexShrink: 0, paddingLeft: 0 }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={values.allowAnyProvider}
                      onChange={() => setFieldValue("allowAnyProvider", !values.allowAnyProvider)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>

                <FormSectionDivider />

                {/* Media */}
                <FormSectionHeader>Service Thumbnail</FormSectionHeader>
                <div className="mb-3">
                  <MediaUploader
                    hint="PNG or JPG. Changes are saved immediately."
                    previewImage={image}
                    uploadHandler={uploadImage}
                    removeHandler={removeImage}
                    onUploadSuccess={fetchImage}
                  />
                </div>

              </Container>
            </Form>
          )
        }}
      </Formik>
  )
})

export default ServiceDetailsPage;
